import { Fetcher, HTTPError, objectToFormData } from '@this/src/util';
import React from 'react';
import _ from 'lodash';
import type { RouteComponentProps } from 'react-router-dom';
import { styled } from '@this/constants/themes';

import SimpleLoading from '@this/components/shared/simple_loading/simple_loading';
// ProjectList クラスが実行時に Project クラスを必要とするため、import typeにできない
// eslint-disable-next-line @typescript-eslint/consistent-type-imports
import Project from '@this/domain/project/project';
import type { ApproveStages, WorkflowType, ProjectArgs } from '@this/domain/project/project';
import User from '@this/domain/user/user';
import type OrganizationMemberJson from '@this/domain/user/organization_member_json';
import { DeleteMasterButton } from '@this/components/organization/delete-master-button';
import { MasterListContainer } from '@this/components/organization/master_list_container';
import type { ProjectFilterSortType } from '@this/components/organization/projects/project_filter';
import { ProjectFilter } from '@this/components/organization/projects/project_filter';
import { ProjectFormModal } from '@this/components/organization/projects/project_form_modal';
import ProjectList from '@this/domain/project/project_list';
import { getColor } from '@this/shared/ui/theme';
import { Link } from '@this/shared/ui/navigations/link';
import { reportError } from '@this/src/lib/bugsnag';
import ProjectSharesBlock from './project_shares_block.template';
import {
  OrganizationBody,
  OrganizationTable,
  OrganizationTh,
  OrganizationButtonTh,
  OrganizationTd,
  OrganizationTdButton,
  AddButton,
  OrganizationTitle
} from '../organization.style';
import Notification from '../../../notification';

type Props = RouteComponentProps;

interface State {
  projects: ProjectList;
  projectShareAvailability: boolean;
  editingProject: Project | null;
  saving: boolean;
  loading: boolean;
  error: string | null;
  mode: string;
  currentPage: number;
  totalPage: number;
  orderType: ProjectFilterSortType | 'CREATED_AT_ASC';
  selectApproveStages: ApproveStages;
  filterFormText: string;
  filterDisabled: boolean;
  showForm: boolean;
  showApproval: boolean;
  useProject: boolean;
}

interface ProjectsResponse {
  projects: ProjectArgs[];
  members: OrganizationMemberJson[];
  projectShares: any;
  project_share_availability: boolean;
  total_page: number;
  show_approval: boolean;
}

interface ProjectResponse {
  project: ProjectArgs & { use_project?: boolean };
}

interface CreateResponse {
  id: number;
}

interface UpdateResponse {}

interface DeleteResponse {}

class Projects extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      projects: new ProjectList([]),
      projectShareAvailability: false,
      editingProject: null,
      saving: false,
      loading: false,
      error: null,
      mode: 'project',
      currentPage: 1,
      totalPage: 0,
      orderType: 'CREATED_AT_ASC',
      selectApproveStages: { default: { 1: [''] } },
      filterFormText: '',
      filterDisabled: false,
      showForm: false,
      showApproval: false,
      useProject: false
    };

    this.handleApproverChange = this.handleApproverChange.bind(this);
    this.handleApproverReset = this.handleApproverReset.bind(this);
    this.handleChangeProjectsRadio = this.handleChangeProjectsRadio.bind(this);
  }

  componentDidMount() {
    this.setState({ loading: true });
    this.fetchProjects();
  }

  fetchProjects = (page = 1, text = '', disabled = false, orderType: ProjectFilterSortType = 'CREATED_AT_ASC') => {
    Fetcher.get<ProjectsResponse>('/organization/projects.json', {
      page,
      text,
      disabled,
      order_type: orderType
    }).then(
      result => {
        const members = _.map(result.members, u => new User(u));
        this.setState({
          projects: new ProjectList(result.projects, members),
          projectShareAvailability: result.project_share_availability,
          loading: false,
          currentPage: page,
          totalPage: result.total_page,
          orderType,
          filterFormText: text,
          filterDisabled: disabled,
          showApproval: result.show_approval
        });
      },
      _error => {
        this.setState({
          error: '通信環境が不安定です。\n時間をおいてもう一度お試しください。',
          loading: false
        });
      }
    );
  };

  handleChangeProjectsRadio(e: React.ChangeEvent<HTMLInputElement>) {
    this.setState({
      mode: e.target.id
    });
  }

  handleApproverChange(i: number, s: number, member: User, workflowType: WorkflowType = 'default') {
    const approveStages = this.state.selectApproveStages;
    approveStages[workflowType][s][i] = member.id;
    this.setApproverIds(approveStages, s, workflowType);
    this.setState({ selectApproveStages: approveStages });
  }

  handleApproverReset(i: number, s: number, workflowType: WorkflowType = 'default') {
    const approveStages = this.state.selectApproveStages;
    approveStages[workflowType][s][i] = '';
    this.setApproverIds(approveStages, s, workflowType);
    this.setState({ selectApproveStages: approveStages });
  }

  setApproverIds(approveStages: any, s: number, workflowType: WorkflowType = 'default') {
    this.state.editingProject?.setApproverIds(
      _.intersection(_.compact(approveStages[workflowType][s])),
      s,
      workflowType
    );
  }

  handleAddClick = () =>
    this.setState({
      showForm: true,
      useProject: false,
      selectApproveStages: { default: { 1: [''] } }
    });

  handleEditClick = (project: any, e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    this.setState({ useProject: false });
    Fetcher.get<ProjectResponse>(`/organization/projects/${project.id}`)
      .then(result => {
        this.setState({
          editingProject: project,
          showForm: true,
          selectApproveStages: _.cloneDeep(project.approveStages),
          useProject: result.project.use_project || false
        });
      })
      .catch(error => {
        this.setState({
          error: '通信環境が不安定です。\n時間をおいてもう一度お試しください。',
          loading: false
        });
      });
  };

  handleFormSubmit = async (newProject: Project) => {
    this.setState({
      saving: true
    });

    try {
      if (newProject.id !== 0) {
        await this.submitUpdate(newProject);
      } else {
        await this.submitCreate(newProject);
      }
      this.fetchProjects();

      this.setState({
        editingProject: null,
        saving: false,
        showForm: false
      });
    } catch (e) {
      let errorMessage = '通信環境が不安定です。\n時間をおいてもう一度お試しください。';
      if (e instanceof HTTPError && (e.response?.status === 400 || e.response?.status === 422)) {
        errorMessage = e.response.data.errors;
      }

      Notification.error(errorMessage);
    }
  };

  submitCreate = async (newProject: Project) => {
    await Fetcher.post<CreateResponse>('/organization/projects', objectToFormData(newProject.updateParams()));
  };

  submitUpdate = async (project: Project) => {
    await Fetcher.put<UpdateResponse>(
      `/organization/projects/${project.id}`,
      objectToFormData(project.updateParams())
    );
  };

  handleCancelClick = () => {
    this.setState({
      editingProject: null,
      showForm: false
    });
  };

  deleteProject(project: Project) {
    this.setState({
      saving: true
    });

    Fetcher.delete<DeleteResponse>(`/organization/projects/${project.id}`, {})
      .then(() => {
        this.fetchProjects();
      })
      .catch(e => {
        let errorMessage = '通信環境が不安定です。\n時間をおいてもう一度お試しください。';
        if (e instanceof HTTPError && (e.response?.status === 400 || e.response?.status === 422)) {
          errorMessage = e.response.data.errors;
        }

        Notification.error(errorMessage);
      })
      .finally(() => {
        this.setState({
          saving: false
        });
      });
  }

  grayIfNeeded = (disabled: boolean) => {
    return {
      background: disabled ? '#f2f2f3' : '#ffffff'
    };
  };

  headerContents = (showApproval: boolean) => {
    if (showApproval) {
      return (
        <>
          <Link href="/organization/projects/csv/bulk_create" style={{ marginRight: '10px' }}>
            一括登録・更新
          </Link>
          <Link href="/organization/projects/csv/bulk_update_approvers">承認者一括追加・更新</Link>
        </>
      );
    }
    return <Link href="/organization/projects/csv/bulk_create">一括登録・更新</Link>;
  };

  render() {
    try {
      const {
        projects,
        projectShareAvailability,
        editingProject,
        saving,
        loading,
        error,
        currentPage,
        totalPage,
        orderType,
        selectApproveStages,
        filterFormText,
        filterDisabled,
        showForm,
        showApproval,
        useProject
      } = this.state;
      return (
        <>
          <OrganizationTitle>プロジェクトマスタ</OrganizationTitle>
          <OrganizationBody>
            {projectShareAvailability && (
              <OrganizationProjectsRadioDiv>
                <OrganizationProjectsRadioInput
                  type="radio"
                  name="mode"
                  id="project"
                  onChange={this.handleChangeProjectsRadio}
                  checked={this.state.mode === 'project'}
                />
                <label htmlFor="project">プロジェクト</label>

                <OrganizationProjectsRadioInput
                  type="radio"
                  name="mode"
                  id="share"
                  onChange={this.handleChangeProjectsRadio}
                  checked={this.state.mode === 'share'}
                />
                <label htmlFor="share">按分</label>
              </OrganizationProjectsRadioDiv>
            )}

            {this.state.mode === 'project' ? (
              <>
                <ProjectFormModal
                  open={showForm}
                  project={editingProject || undefined}
                  members={projects.members}
                  approveStages={selectApproveStages}
                  loading={saving}
                  onClose={this.handleCancelClick}
                  onSubmit={this.handleFormSubmit}
                  showApproval={showApproval}
                  useProject={useProject}
                  handleApproverChange={this.handleApproverChange}
                  handleApproverReset={this.handleApproverReset}
                />

                <ProjectFilter
                  onSearch={(text, disabled, orderType) => this.fetchProjects(1, text, disabled, orderType)}
                />
                <MasterListContainer
                  currentPage={currentPage}
                  totalPage={totalPage}
                  onPaginate={page => this.fetchProjects(page, filterFormText, filterDisabled, orderType)}
                  createButton={
                    <AddButton onClick={this.handleAddClick} disabled={!!editingProject}>
                      プロジェクトを追加
                    </AddButton>
                  }
                  headerContent={<div>{this.headerContents(showApproval)}</div>}
                  renderContent={() => {
                    return (
                      <OrganizationTable>
                        <tbody>
                          <tr>
                            <OrganizationTh>プロジェクトコード</OrganizationTh>
                            <OrganizationTh>プロジェクト名</OrganizationTh>
                            <OrganizationTh>日当対象</OrganizationTh>
                            {showApproval && <OrganizationTh>承認者</OrganizationTh>}
                            <OrganizationButtonTh>編集</OrganizationButtonTh>
                            <OrganizationButtonTh>削除</OrganizationButtonTh>
                          </tr>
                          {loading ? (
                            <tr>
                              <td colSpan={11}>
                                <SimpleLoading />
                              </td>
                            </tr>
                          ) : error ? (
                            <tr>
                              <td colSpan={11}>
                                <Error>{error}</Error>
                              </td>
                            </tr>
                          ) : (
                            <>
                              {projects.list.map((p: Project) => (
                                <tr style={this.grayIfNeeded(p.disabled)} key={p.id} data-e2e-id={p.id}>
                                  <OrganizationTd className="e2e-code">{p.code}</OrganizationTd>
                                  <OrganizationTd className="e2e-name">{p.name}</OrganizationTd>
                                  <OrganizationTd className="e2e-allowance-target">
                                    {p.allowanceTarget === false ? '対象外' : '対象'}
                                  </OrganizationTd>
                                  {showApproval && (
                                    <OrganizationTd className="e2e-approver">
                                      {p.approvers.length > 0 && p.approvers[0].email}
                                    </OrganizationTd>
                                  )}
                                  <OrganizationTd>
                                    {!editingProject && (
                                      <OrganizationTdButton onClick={e => this.handleEditClick(p, e)}>
                                        編集
                                      </OrganizationTdButton>
                                    )}
                                  </OrganizationTd>
                                  <OrganizationTd>
                                    <DeleteMasterButton
                                      className="e2e-confirm-delete"
                                      targetLabel={p.name}
                                      onConfirm={() => {
                                        this.deleteProject(p);
                                      }}
                                    />
                                  </OrganizationTd>
                                </tr>
                              ))}
                            </>
                          )}
                        </tbody>
                      </OrganizationTable>
                    );
                  }}
                />
              </>
            ) : (
              <ProjectSharesBlock />
            )}
          </OrganizationBody>
        </>
      );
    } catch (e) {
      reportError(e);
      return null;
    }
  }
}

const Error = styled.div`
  color: ${props => props.theme.redColor};
`;

const OrganizationProjectsRadioDiv = styled.div`
  height: 40px;
  padding-top: 10px;
`;

const OrganizationProjectsRadioInput = styled.input`
  && {
    display: none;
    + label {
      padding-left: 20px;
      position: relative;
      float: left;
      margin-right: 10px;
      &::before {
        content: '';
        display: block;
        position: absolute;
        top: 1px;
        left: 0;
        width: 15px;
        height: 15px;
        border: 1px solid #999;
        border-radius: 50%;
      }
    }
    &:checked {
      + label {
        color: ${getColor('brand', 'primary')};
        &::after {
          content: '';
          display: block;
          position: absolute;
          top: 3px;
          left: 2px;
          width: 11px;
          height: 11px;
          background: ${getColor('brand', 'primary')};
          border-radius: 50%;
        }
      }
    }
  }
`;

export default Projects;
